import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="forgot-password-page">
      <div className="overline mb-3">Account recovery</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight mb-8">Reset your password</h1>
      <Card className="p-7 rounded-2xl border-border shadow-none">
        {submitted ? (
          <div className="text-center py-6" data-testid="forgot-success">
            <div className="overline mb-3 text-primary">Check your email</div>
            <h2 className="font-serif text-2xl font-semibold tracking-tight">If that email exists,</h2>
            <p className="mt-2 text-sm text-muted-foreground">a reset link is on its way. The link expires in 2 hours.</p>
            <Link to="/login"><Button variant="ghost" className="mt-6 rounded-full" data-testid="back-to-login">Back to login</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="fpemail">Email</Label>
              <Input id="fpemail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full cta-saffron" data-testid="forgot-submit">
              {loading ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
      </Card>
      <p className="text-sm text-muted-foreground mt-6">
        Remembered it? <Link to="/login" className="text-foreground underline underline-offset-4">Log in</Link>
      </p>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const initialToken = params.get("token") || "";
  const [token, setToken] = useState(initialToken);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — please log in");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-password-page">
      <div className="overline mb-3">Set a new password</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight mb-8">New password</h1>
      <Card className="p-7 rounded-2xl border-border shadow-none">
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="rtok">Reset token</Label>
            <Input id="rtok" value={token} onChange={(e) => setToken(e.target.value)} required data-testid="reset-token-input" />
            <p className="text-[11px] text-muted-foreground">Paste the token from your email link (or your admin)</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rpw">New password</Label>
            <Input id="rpw" type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-pw-input" />
            <p className="text-[11px] text-muted-foreground">Min 8 characters · must include letters + numbers</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rpw2">Confirm password</Label>
            <Input id="rpw2" type="password" minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-pw2-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full cta-saffron" data-testid="reset-submit">
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
